import { useEffect, useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments, faArrowUp, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import PlusBadge from "./PlusBadge";
import styles from "./ChatModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import api from "../services/api";

const MENTION_DEBOUNCE_MS = 300;

// Detecta se o cursor está "dentro" de um @token sendo digitado - olha pra
// trás a partir do cursor até achar um "@" que comece o token (início do
// texto ou precedido de espaço) sem nenhum espaço entre ele e o cursor.
// Retorna null quando não está em modo menção (ex: "fulano@dominio.com" não
// dispara, porque o "@" ali não é precedido de espaço/início).
function detectMentionToken(text, cursor) {
  const upToCursor = text.slice(0, cursor);
  const at = upToCursor.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(upToCursor[at - 1])) return null;
  const query = upToCursor.slice(at + 1);
  if (/\s/.test(query)) return null; // já passou do token (digitou espaço depois do @)
  return { anchor: at, query };
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Destaca "@Nome" dentro do texto da mensagem usando o array `mentions` que
// vem junto (backend resolve os nomes atuais, não fica preso ao que foi
// digitado na hora do envio). Sem markup escondido no content - a mensagem
// guardada é texto puro, o destaque é só de exibição.
function renderMessageContent(content, mentions) {
  if (!mentions || mentions.length === 0) return content;
  const tokens = [...new Set(mentions.filter((m) => m.name).map((m) => `@${m.name}`))];
  if (tokens.length === 0) return content;

  const pattern = new RegExp(`(${tokens.map(escapeRegExp).join("|")})`, "g");
  return content.split(pattern).map((part, i) =>
    tokens.includes(part) ? (
      <span key={i} className={styles.mentionTag}>{part}</span>
    ) : (
      part
    )
  );
}

function avatarFor(name, url) {
  if (!url) {
    return `https://ui-avatars.com/api/?background=1d9e75&color=fff&name=${encodeURIComponent(name || "?")}`;
  }
  // URLs absolutas (Google) usam-se como estão, reduzindo o tamanho pedido
  // pra evitar 429 (mesmo padrão de TeacherDashboard.jsx); caminhos locais
  // (uploads/avatars/...) precisam do host do backend na frente — mesmo
  // padrão de resolveAvatarUrl (Dashboard.js) / buildImageSrc (UserAdminModal.jsx).
  if (/^https?:\/\//i.test(url)) return url.replace(/=s\d+-c/, "=s40-c");
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  return `${base}/${url.replace(/^\/+/, "")}`;
}

const STATUS_LABEL = {
  open: "Conectado",
  connecting: "Conectando...",
  closed: "Reconectando...",
  idle: "",
};

/**
 * ChatModal — UI de apresentação da sala de chat. A conexão WS/estado vivem
 * em useChatEntry (Dashboard.js), mantida ativa mesmo com o modal fechado,
 * para que o badge de não-lido no ícone do header funcione sem o modal aberto.
 */
export default function ChatModal({
  open,
  onClose,
  currentUserId,
  roomId,
  showTabs,
  activeTab,
  onTabChange,
  status,
  messages,
  hasMoreHistory,
  loadingHistory,
  typingUsers,
  loadOlderMessages,
  sendMessage,
  sendTyping,
}) {
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);
  useBodyScrollLock(open);
  const bottomRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const prevScrollHeightRef = useRef(null);
  const inputRef = useRef(null);

  // @menções - ver detectMentionToken acima. `mentions` guarda só as que
  // nasceram de uma seleção real no dropdown (nunca inferidas re-parseando
  // o texto), filtradas na hora do envio pelo texto "@Nome" ainda estar
  // presente no draft (ver handleSubmit).
  const [mentions, setMentions] = useState([]);
  const [mentionToken, setMentionToken] = useState(null); // { anchor, query } | null
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [mentionLoading, setMentionLoading] = useState(false);
  const mentionDebounceRef = useRef(null);
  const mentionRequestSeqRef = useRef(0);

  useEffect(() => {
    if (open) {
      setDraft("");
      setMentions([]);
      setMentionToken(null);
    }
  }, [open, activeTab]);

  // Busca candidatos a @menção conforme o usuário digita - debounced, com
  // proteção contra resposta fora de ordem (seq) quando o texto muda rápido.
  useEffect(() => {
    clearTimeout(mentionDebounceRef.current);
    if (mentionToken == null || !roomId) {
      setMentionResults([]);
      return undefined;
    }
    const seq = ++mentionRequestSeqRef.current;
    setMentionLoading(true);
    mentionDebounceRef.current = setTimeout(() => {
      api
        .get(`/chat/rooms/${roomId}/members`, { params: { q: mentionToken.query || undefined } })
        .then((res) => {
          if (seq !== mentionRequestSeqRef.current) return; // resposta obsoleta
          setMentionResults(res.data);
          setMentionActiveIndex(0);
        })
        .catch(() => {
          if (seq === mentionRequestSeqRef.current) setMentionResults([]);
        })
        .finally(() => {
          if (seq === mentionRequestSeqRef.current) setMentionLoading(false);
        });
    }, MENTION_DEBOUNCE_MS);
    return () => clearTimeout(mentionDebounceRef.current);
  }, [mentionToken, roomId]);

  const mentionDropdownOpen = mentionToken != null && (mentionLoading || mentionResults.length > 0);

  const selectMention = useCallback((candidate) => {
    if (!mentionToken) return;
    const el = inputRef.current;
    const insertText = `@${candidate.name} `;
    setDraft((prev) => {
      const before = prev.slice(0, mentionToken.anchor);
      const after = prev.slice(mentionToken.anchor + 1 + mentionToken.query.length);
      const next = `${before}${insertText}${after}`;
      // Reposiciona o cursor logo depois do texto inserido, no próximo tick
      // (setSelectionRange precisa do value já aplicado no DOM).
      const cursorPos = before.length + insertText.length;
      requestAnimationFrame(() => el?.setSelectionRange(cursorPos, cursorPos));
      return next;
    });
    setMentions((prev) => (prev.some((m) => m.id === candidate.id) ? prev : [...prev, { id: candidate.id, name: candidate.name }]));
    setMentionToken(null);
    el?.focus();
  }, [mentionToken]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    if (prevScrollHeightRef.current != null) {
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = null;
      return;
    }

    if (shouldAutoScrollRef.current) {
      bottomRef.current?.scrollIntoView({ block: "end" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, open]);

  if (!open) return null;

  function handleScroll() {
    const el = listRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = distanceFromBottom < 80;

    if (el.scrollTop < 60 && hasMoreHistory && !loadingHistory) {
      prevScrollHeightRef.current = el.scrollHeight;
      loadOlderMessages();
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    // Só manda como menção quem ainda aparece como "@Nome" no texto final -
    // se o aluno apagou/editou o token depois de escolher no dropdown, a
    // menção correspondente cai fora em vez de notificar alguém que não
    // está mais mencionado de verdade no texto.
    const activeMentionIds = mentions
      .filter((m) => draft.includes(`@${m.name}`))
      .map((m) => m.id);
    if (sendMessage(draft, activeMentionIds)) {
      setDraft("");
      setMentions([]);
      setMentionToken(null);
    }
  }

  function handleChange(e) {
    const value = e.target.value;
    setDraft(value);
    sendTyping();
    setMentionToken(detectMentionToken(value, e.target.selectionStart ?? value.length));
  }

  function handleKeyDown(e) {
    if (!mentionDropdownOpen || mentionResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionActiveIndex((i) => (i + 1) % mentionResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionActiveIndex((i) => (i - 1 + mentionResults.length) % mentionResults.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      selectMention(mentionResults[mentionActiveIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMentionToken(null);
    }
  }

  const typingLabel =
    typingUsers.length === 1
      ? `${typingUsers[0].userName} está digitando...`
      : typingUsers.length > 1
      ? `${typingUsers.length} pessoas digitando...`
      : null;

  let lastAuthorId = null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <ModalHeader onClose={onClose} />
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.icon}>
              <FontAwesomeIcon icon={faComments} />
            </span>
            <div>
              <h2 className={styles.title}>Comunidade</h2>
              <p className={styles.sub}>{STATUS_LABEL[status] || ""}</p>
            </div>
          </div>
        </div>

        {showTabs && (
          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === "professor" ? styles.tabActive : ""}`}
              onClick={() => onTabChange("professor")}
            >
              Minha Turma
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === "global" ? styles.tabActive : ""}`}
              onClick={() => onTabChange("global")}
            >
              Comunidade TreinoNexo
            </button>
          </div>
        )}

        <div className={styles.body} ref={listRef} onScroll={handleScroll}>
          {loadingHistory && messages.length === 0 && (
            <div className={styles.loadingMsg}>Carregando mensagens...</div>
          )}
          {!loadingHistory && messages.length === 0 && (
            <div className={styles.emptyMsg}>Nenhuma mensagem ainda. Diga oi!</div>
          )}
          {hasMoreHistory && messages.length > 0 && (
            <button
              type="button"
              className={styles.loadMoreBtn}
              onClick={loadOlderMessages}
              disabled={loadingHistory}
            >
              <FontAwesomeIcon icon={faChevronUp} /> Carregar mensagens anteriores
            </button>
          )}

          {messages.map((m) => {
            const isOwn = m.user_id === currentUserId;
            const groupStart = lastAuthorId !== m.user_id;
            lastAuthorId = m.user_id;
            return (
              <div
                key={m.id}
                className={`${styles.messageRow} ${isOwn ? styles.own : ""} ${
                  groupStart ? styles.groupStart : ""
                }`}
              >
                {!isOwn && (
                  <span
                    className={styles.avatarWrap}
                    style={{ visibility: groupStart ? "visible" : "hidden" }}
                  >
                    <img
                      className={styles.avatar}
                      src={avatarFor(m.user_name, m.user_avatar)}
                      alt=""
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                    {m.user_is_plus && <PlusBadge size={12} title={`${m.user_name} é assinante Plus`} />}
                  </span>
                )}
                <div className={styles.bubbleCol}>
                  {groupStart && !isOwn && (
                    <span className={styles.authorName}>{m.user_name}</span>
                  )}
                  <div
                    className={`${styles.bubble} ${
                      m.mentions?.some((x) => x.id === currentUserId) ? styles.bubbleMentioned : ""
                    }`}
                  >
                    {renderMessageContent(m.content, m.mentions)}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div className={styles.typingSlot}>
          {typingLabel && <span className={styles.typing}>{typingLabel}</span>}
        </div>

        <form className={styles.inputRow} onSubmit={handleSubmit}>
          <div className={styles.inputWrap}>
            {mentionDropdownOpen && (
              <div className={styles.mentionDropdown} role="listbox">
                {mentionLoading && mentionResults.length === 0 ? (
                  <div className={styles.mentionEmpty}>Buscando...</div>
                ) : mentionResults.length === 0 ? (
                  <div className={styles.mentionEmpty}>Ninguém encontrado</div>
                ) : (
                  mentionResults.map((candidate, i) => (
                    <button
                      key={candidate.id}
                      type="button"
                      role="option"
                      aria-selected={i === mentionActiveIndex}
                      className={`${styles.mentionItem} ${i === mentionActiveIndex ? styles.mentionItemActive : ""}`}
                      onMouseDown={(e) => { e.preventDefault(); selectMention(candidate); }}
                      onMouseEnter={() => setMentionActiveIndex(i)}
                    >
                      <span className={styles.mentionAvatarWrap}>
                        <img
                          className={styles.mentionAvatar}
                          src={avatarFor(candidate.name, candidate.avatar_url)}
                          alt=""
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                        {candidate.is_plus && <PlusBadge size={10} title={`${candidate.name} é assinante Plus`} />}
                      </span>
                      <span>{candidate.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            <input
              ref={inputRef}
              type="text"
              className={styles.input}
              placeholder="Digite uma mensagem..."
              value={draft}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              maxLength={2000}
            />
          </div>
          <button type="submit" className={styles.sendBtn} disabled={!draft.trim()}>
            <FontAwesomeIcon icon={faArrowUp} />
          </button>
        </form>
      </div>
    </div>
  );
}
